import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fmtNoteStamp, localToday } from '@quadro/domain';
import { hud } from '../../components/Hud';
import { Sheet, SheetFooter, useAutoFocus } from '../../components/Sheet';
import * as api from '../../data/api';
import { useAction } from '../../data/queries';
import { closeSheet } from '../../state/sheets';

const KEY = ['tokens'] as const;

/**
 * Acesso pelo Claude (MCP): tokens pessoais. O Claude age com as permissões da própria pessoa;
 * "somente leitura" deixa só as consultas. O token aparece uma vez, na criação.
 */
export function ClaudeAccessSheet() {
  const tokens = useQuery({ queryKey: KEY, queryFn: api.fetchTokens });
  const [name, setName] = useState('Claude Code');
  const [readOnly, setReadOnly] = useState(false);
  const [created, setCreated] = useState<{ token: string; name: string } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const create = useAction(api.createToken, [KEY]);
  const revoke = useAction(api.revokeToken, [KEY]);
  const ref = useAutoFocus<HTMLInputElement>();
  const url = `${location.origin}/api/mcp`;
  const command = created ? `claude mcp add --transport http quadro ${url} --header "Authorization: Bearer ${created.token}"` : '';

  async function generate() {
    if (!name.trim()) return;
    const r = await create(name.trim(), readOnly) as { token: string } | undefined;
    if (r) setCreated({ token: r.token, name: name.trim() });
  }
  async function copy(text: string, what: string) {
    try { await navigator.clipboard.writeText(text); hud(`${what} copiado`); } catch { hud('Selecione o texto e copie com Ctrl+C', 'info'); }
  }

  return (
    <Sheet onClose={closeSheet} label="Conectar ao Claude">
      <div className="sh-body cl">
        <h2>Conectar ao Claude</h2>
        <p className="muted">
          O Claude consulta e atualiza o Quadro por você: cria e move tarefas, lista o que está atrasado, mostra a agenda.
          Ele tem as mesmas permissões que você tem aqui.
        </p>

        <h3>No Claude (site, aplicativo ou celular)</h3>
        <ol className="cl-steps">
          <li>No Claude, abra <b>Configurações → Conectores → Adicionar conector personalizado</b>.</li>
          <li>Dê o nome <b>Quadro</b> e cole este endereço:
            <div className="cl-copy">
              <input readOnly value={url} aria-label="Endereço do conector" onFocus={e => e.currentTarget.select()} />
              <button className="btn" onClick={() => copy(url, 'Endereço')}>Copiar</button>
            </div>
          </li>
          <li>Clique em <b>Conectar</b>, entre com a Microsoft e autorize. Pronto: o acesso aparece na lista abaixo.</li>
        </ol>

        <h3>No Claude Code ou outro cliente MCP (com token)</h3>

        {created ? (
          <div className="cl-new" role="status">
            <strong>Token “{created.name}” criado. Copie agora: ele não aparece de novo.</strong>
            <div className="cl-copy">
              <input readOnly value={created.token} aria-label="Token" onFocus={e => e.currentTarget.select()} />
              <button className="btn" onClick={() => copy(created.token, 'Token')}>Copiar</button>
            </div>
            <span className="muted">No Claude Code, rode este comando no terminal:</span>
            <div className="cl-copy">
              <input readOnly value={command} aria-label="Comando do Claude Code" onFocus={e => e.currentTarget.select()} />
              <button className="btn" onClick={() => copy(command, 'Comando')}>Copiar</button>
            </div>
            <span className="muted">Em outro cliente MCP: endereço <code>{url}</code>, cabeçalho <code>Authorization: Bearer &lt;token&gt;</code>.</span>
          </div>
        ) : (
          <div className="form">
            <label htmlFor="clNome">Nome do token</label>
            <input id="clNome" ref={ref} maxLength={60} value={name} onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void generate(); }} placeholder="Ex.: Claude Code no notebook" />
            <span />
            <label className="switch"><input type="checkbox" checked={readOnly} onChange={e => setReadOnly(e.target.checked)} /> Somente leitura (o Claude só consulta)</label>
          </div>
        )}

        <h3>Tokens ativos</h3>
        {tokens.data?.length ? tokens.data.map(t => (
          <div className="pp-row" key={t.id}>
            <span className="nm">{t.name}<br /><span className="muted"><code>{t.prefix}…</code>{t.readOnly ? ' · somente leitura' : ''}</span></span>
            <span className="muted">{t.lastUsedAt ? `usado ${fmtNoteStamp(t.lastUsedAt, localToday())}` : 'nunca usado'}</span>
            {confirming === t.id
              ? <button className="btn danger" onClick={() => { setConfirming(null); void revoke(t.id); hud('Token revogado'); }}>Confirmar</button>
              : <button className="btn" onClick={() => setConfirming(t.id)}>Revogar</button>}
          </div>
        )) : <p className="muted">{tokens.isPending ? 'Carregando…' : 'Nenhum token ainda.'}</p>}
      </div>
      <SheetFooter>
        <span className="grow" />
        <button className="btn" onClick={closeSheet}>Fechar</button>
        {!created && <button className="btn primary" disabled={!name.trim()} onClick={generate}>Gerar token</button>}
        {created && <button className="btn primary" onClick={() => setCreated(null)}>Gerar outro</button>}
      </SheetFooter>
    </Sheet>
  );
}
